<div class="relative inline-block w-full" [class.w-auto]="variant === 'pill'" [ngClass]="customClass">
  <!-- VARIANT: PILL -->
  @if (variant === 'pill') {
    <button
      type="button"
      role="combobox"
      [attr.aria-expanded]="isOpen()"
      aria-haspopup="listbox"
      [attr.id]="id || null"
      (click)="toggleDropdown()"
      [disabled]="disabled"
      [class]="'flex justify-between items-center px-3 py-1 rounded-[99px] border-[1px] border-solid border-brand-border bg-surface-card hover:border-primary-200 hover:bg-surface/80 outline-none focus:outline-none focus:border-primary-300 focus:shadow-focus transition-[background-color,border-color,box-shadow] cursor-pointer shadow-xs h-[32px] ' + pillClass"
      [ngClass]="{
        'border-primary-300': hasSelection || isOpen(),
        'shadow-focus': isOpen(),
        'opacity-50 cursor-not-allowed': disabled
      }"
    >
      <span class="text-brand-muted font-medium text-xs leading-normal tracking-normal truncate">
        {{ label }}
      </span>

      @if (summary) {
        <span class="bg-surface text-primary text-xs font-semibold px-2 py-0.5 rounded-md leading-normal truncate max-w-[120px]">
          {{ summary }}
        </span>
      }

      <app-icon-chevron [class.rotate-180]="isOpen()" class="transition-transform duration-200 shrink-0"></app-icon-chevron>
    </button>
  }

  <!-- VARIANT: FORM -->
  @if (variant === 'form') {
    @if (label) {
      <div class="flex items-center justify-between mb-1">
        <label [attr.for]="id || null" class="block text-sm font-medium text-stone-700">
          {{ label }}
        </label>
        <ng-content select="[label-suffix]"></ng-content>
      </div>
    }

    <button
      type="button"
      role="combobox"
      [attr.aria-expanded]="isOpen()"
      aria-haspopup="listbox"
      [attr.id]="id || null"
      (click)="toggleDropdown($event)"
      [disabled]="disabled"
      class="w-full flex justify-between items-center bg-surface-header/60 border-[1px] border-solid rounded-lg text-stone-800 outline-none transition-[background-color,border-color,box-shadow] py-2 px-3.5 text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      [ngClass]="[
        error
          ? 'border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/15'
          : (isOpen()
              ? 'border-primary-300 shadow-focus bg-white'
              : 'border-brand-border hover:border-primary-200 focus:border-primary-300 focus:shadow-focus')
      ]"
    >
      <span class="truncate" [ngClass]="hasSelection ? 'text-stone-800' : 'text-stone-400'">
        {{ formDisplayText || placeholder }}
      </span>

      <div class="flex items-center gap-1.5 text-stone-400">
        @if (clearable && hasSelection && !disabled) {
          <span
            (click)="clearSelection($event)"
            class="hover:text-stone-600 p-0.5 cursor-pointer rounded-full transition-colors"
            title="Effacer"
          >
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </span>
        }
        <app-icon-chevron [class.rotate-180]="isOpen()" class="transition-transform duration-200"></app-icon-chevron>
      </div>
    </button>

    @if (reserveErrorSpace) {
      <div class="h-5 min-h-[1.25rem] flex items-center mt-1">
        @if (error) {
          <span class="inline-flex items-center gap-1 text-xs text-red-600 font-medium animate-fade-in">
            <svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{{ error }}</span>
          </span>
        }
      </div>
    } @else if (error) {
      <span class="inline-flex items-center gap-1 text-xs text-red-600 font-medium mt-1 animate-fade-in">
        <svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>{{ error }}</span>
      </span>
    }
  }

  <!-- POPOVER DROPDOWN -->
  @if (isOpen()) {
    <div
      (click)="$event.stopPropagation()"
      [class]="'absolute top-full mt-2 z-50 bg-white border border-gray-200 rounded-2xl shadow-xl p-3 animate-in fade-in zoom-in-95 duration-100 ' + computedDropdownClasses"
    >
      @if (searchable) {
        <div class="mb-2">
          <input
            type="text"
            [ngModel]="searchTerm()"
            (ngModelChange)="onSearchChange($event)"
            [placeholder]="resolvedSearchPlaceholder"
            class="w-full px-3 py-1.5 text-xs bg-surface-header/60 border-[1px] border-solid border-brand-border rounded-lg outline-none hover:border-primary-200 focus:bg-white focus:border-primary-300 focus:shadow-focus transition-[background-color,border-color,box-shadow]"
          />
        </div>
      }

      <div role="listbox" [attr.aria-multiselectable]="multiple" class="max-h-52 overflow-y-auto space-y-1">
        @for (item of filteredItems; track getItemTrackKey(item, $index)) {
          <div
            role="option"
            [attr.aria-selected]="isSelected(item)"
            (click)="onItemClick(item, $event)"
            class="flex items-center gap-2.5 px-2 py-1.5 hover:bg-surface rounded-lg cursor-pointer text-sm text-stone-700 select-none transition-colors"
            [ngClass]="{'text-primary bg-surface': isSelected(item)}"
          >
            @if (multiple) {
              <input
                type="checkbox"
                [checked]="isSelected(item)"
                tabindex="-1"
                class="w-4 h-4 accent-primary text-primary bg-white border-brand-border rounded focus:ring-primary pointer-events-none transition-shadow"
              />
            } @else {
              <input
                type="radio"
                [checked]="isSelected(item)"
                tabindex="-1"
                class="w-4 h-4 accent-primary text-primary bg-white border-brand-border rounded focus:ring-primary pointer-events-none transition-shadow"
              />
            }
            <span class="truncate">{{ getItemLabel(item) }}</span>
          </div>
        }

        @if (filteredItems.length === 0) {
          <div class="p-2 text-xs text-gray-500 text-center">
            {{ resolvedEmptyMessage }}
          </div>
        }
      </div>
    </div>
  }
</div>
